import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, MessageSquare, Search, RefreshCw, AlertCircle } from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import DataTable from '../components/dashboard/DataTable';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const AdminReviews = () => {
    const { token } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/api/admin/reviews`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReviews(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load reviews');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredReviews = reviews.filter(r => 
        r.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.comment?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
        {
            header: 'Product',
            accessor: 'product_name',
            cell: (row) => (
                <div>
                    <p className="font-medium text-sm">{row.product_name}</p>
                    <p className="text-xs text-muted-foreground truncate w-[150px]">{row.product_id}</p>
                </div>
            )
        },
        {
            header: 'Customer',
            accessor: 'user_name',
            cell: (row) => <span className="font-medium">{row.user_name}</span>
        },
        {
            header: 'Rating',
            accessor: 'rating',
            cell: (row) => (
                <div className="flex items-center gap-1 text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded w-fit font-medium border border-orange-200">
                    <Star className="w-3.5 h-3.5 fill-current" />
                    {row.rating}.0
                </div>
            )
        },
        {
            header: 'Comment',
            accessor: 'comment',
            cell: (row) => (
                <div className="flex items-start gap-2 max-w-[400px]">
                    <MessageSquare className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground line-clamp-2" title={row.comment}>
                        {row.comment || <span className="italic text-muted-foreground/50">No text provided</span>}
                    </p>
                </div>
            )
        },
        {
            header: 'Date',
            accessor: 'created_at',
            cell: (row) => <span className="whitespace-nowrap text-sm">{new Date(row.created_at).toLocaleDateString()}</span>
        }
    ];

    return (
        <DashboardLayout userRole="admin">
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Global Reviews</h1>
                    <p className="text-muted-foreground mt-2">Monitor all customer feedback explicitly left on products universally across the platform.</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="relative w-full sm:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search reviews, products, or customers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Button variant="outline" onClick={fetchReviews} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                <div className="bg-card rounded-xl border shadow-sm flex flex-col min-h-[500px]">
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center min-h-[300px]">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : filteredReviews.length > 0 ? (
                        <DataTable columns={columns} data={filteredReviews} />
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center p-12 min-h-[300px]">
                            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                                <AlertCircle className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">No Reviews Found</h3>
                            <p className="text-muted-foreground max-w-sm">
                                {searchTerm ? 'No reviews match your search query.' : 'There are no product reviews available on the platform yet.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AdminReviews;
